import { MouseEvent } from "react";

import {
  checkHasResponse,
  isDigitalSignatureField,
} from "@smart/bridge-types-basic";
import { Loading } from "@smart/itops-sb-design-system-dom";
import { isNotNullOrUndefined } from "@smart/itops-utils-basic";

import {
  Section,
  SectionButtons,
  PageWrapper,
  SectionForm,
  Page,
} from "./section-components";
import { ErrorSummary } from "./shared-components/error-summary";
import { PreviewFormFooter } from "./shared-components/preview-form-footer";
import {
  SummaryField,
  SummaryGroup,
  SummaryGroupFieldSet,
  SummarySection,
} from "./summary-components";
import { renderOrderedItems } from "../hooks";
import {
  AutoFillStatus,
  FieldItem,
  GroupItem,
  OrderedGroup,
  OrderedSection,
  SectionItem,
  TeamItem,
  Toast,
} from "../types";
import { getTranslation } from "../utils/translation";
import { getUITranslation, validationKeys } from "../utils/ui-translations";

type IndexedFieldItem = FieldItem & { groupIndex?: number };

const translateGroup = (
  group: GroupItem,
  selectedLanguage?: string,
): GroupItem => {
  const translation = getTranslation(group.translations, selectedLanguage);
  return {
    ...group,
    label: translation?.label || group.label,
    description: translation?.description || group.description,
    repeatPrompt: translation?.repeatPrompt || group.repeatPrompt,
  };
};

const translateField = (
  field: FieldItem,
  selectedLanguage?: string,
): FieldItem => {
  const translation = getTranslation(field.translations, selectedLanguage);
  return {
    ...field,
    label: translation?.label || field.label,
    description: translation?.description || field.description,
    options: translation?.options || field.options,
  };
};

export type IntakeSummaryProps = {
  team: TeamItem;
  visibleSections: OrderedSection[];
  responses: Record<string, any>;
  isLoadingDBResponses: boolean;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  onSelect?: (section: SectionItem) => void;
  onSubmitButton?: (e: MouseEvent<HTMLButtonElement>) => void;
  onSubmit?: () => void;
  onReset?: () => void;
  submitError?: string;
  isForm?: boolean;
  hasDigitalSignature: boolean;
  previewErrorToggle?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  autoFillingStatus?: AutoFillStatus;
  isPreview?: boolean;
  isTeamHidden?: boolean;
  setToasts?: (toasts: Toast[]) => void;
  selectedLanguage?: string;
};

const noop = () => {};

export const IntakeSummary = ({
  team,
  visibleSections,
  responses,
  isLoadingDBResponses,
  sidebar,
  setSidebar,
  onSelect,
  onSubmitButton,
  onSubmit,
  onReset,
  hasDigitalSignature,
  submitError,
  isForm = true,
  previewErrorToggle,
  autoFillingStatus,
  isPreview,
  isTeamHidden,
  setToasts,
  selectedLanguage,
}: IntakeSummaryProps) => {
  const getResponseForField = (
    field: IndexedFieldItem,
    responsesMap: Record<string, any>,
  ) => {
    if (!field.groupUri) return responsesMap[field.uri];

    const fieldResp = responsesMap[field.uri];

    return Array.isArray(fieldResp)
      ? fieldResp[field.groupIndex ?? 0]
      : fieldResp;
  };

  const missingRequired = visibleSections
    .map(({ orderedItems }) =>
      orderedItems.map(
        (item) =>
          (item.type === "group"
            ? item.groupFieldSets
                .map((fieldSet) =>
                  fieldSet.visibleFields.map((fs) => ({
                    ...fs,
                    groupIndex: fieldSet.index,
                  })),
                )
                .flat()
            : [item.field]) as IndexedFieldItem[],
      ),
    )
    .flat(2)
    .filter(
      (field) =>
        field.mandatory &&
        !isDigitalSignatureField(field) &&
        !checkHasResponse(field.type, getResponseForField(field, responses)),
    );

  const onGotoError = (sectionUri: string) => {
    const section = visibleSections.find((s) => s.section.uri === sectionUri);
    if (section) {
      onSelect?.({
        ...section.section,
        validateOnDisplay: true,
      });
    }
  };

  const requiredErrorMessage = getUITranslation(
    validationKeys.requiredQuestion,
    selectedLanguage,
  );

  const fieldErrors = missingRequired
    .filter((field) => !field.groupUri)
    .map((field) => ({
      name: field.uri,
      label: translateField(field, selectedLanguage).label,
      error: requiredErrorMessage,
      order: field.order,
      sectionIndex: visibleSections.findIndex(
        (s) => s.section.uri === field.sectionUri,
      ),
      onClick: () => onGotoError(field.sectionUri),
    }));

  const groupErrors = visibleSections
    .flatMap((section) => section.orderedItems)
    .filter((orderedItem) => orderedItem.type === "group")
    .map((orderedItem) => {
      const orderedGroup = orderedItem as unknown as OrderedGroup;

      return orderedGroup.groupFieldSets.map((fieldSet) => {
        const index = fieldSet.index || 0;
        const groupFieldErrors = fieldSet.visibleFields
          .map((field) =>
            field.mandatory &&
            !isDigitalSignatureField(field) &&
            !checkHasResponse(field.type, responses[field.uri]?.[index])
              ? {
                  name: `${field.uri}-${index}`,
                  label: translateField(field, selectedLanguage).label,
                  error: requiredErrorMessage,
                  onClick: () => onGotoError(field.sectionUri),
                }
              : undefined,
          )
          .filter(isNotNullOrUndefined);

        return groupFieldErrors.length
          ? {
              name: orderedGroup.group.repeatable
                ? `${orderedGroup.group.uri}-${index}`
                : orderedGroup.group.uri,
              index: orderedGroup.group.repeatable ? index : undefined,
              label: translateGroup(orderedGroup.group, selectedLanguage).label,
              order: orderedGroup.fields[0].order,
              sectionIndex: visibleSections.findIndex(
                (s) => s.section.uri === orderedGroup.group.sectionUri,
              ),
              fieldErrors: groupFieldErrors,
            }
          : undefined;
      });
    })
    .flat()
    .filter(isNotNullOrUndefined);

  return (
    <PageWrapper sidebar={sidebar}>
      <Page
        isPreview={isPreview}
        className={previewErrorToggle && "mb-[5.6rem]"}
      >
        {isLoadingDBResponses && <Loading />}
        <Section>
          <SectionForm onSubmit={onSubmit} onReset={onReset} isForm={isForm}>
            <SectionButtons
              isForm={isForm}
              team={team}
              hasError={submitError}
              hasDigitalSignature={hasDigitalSignature}
              setSidebar={setSidebar}
              onPrevious={
                onSelect
                  ? () =>
                      onSelect(
                        visibleSections[visibleSections.length - 1].section,
                      )
                  : undefined
              }
              onSubmit={onSubmitButton || noop}
              disabled={{
                submit:
                  (!previewErrorToggle?.[0] && !!missingRequired.length) ||
                  autoFillingStatus === "filling",
              }}
              showSectionsButton
              isTeamHidden={isTeamHidden}
              setToasts={setToasts}
              selectedLanguage={selectedLanguage}
            >
              <header className="flex flex-col gap-small">
                <h1 className="font-semibold text-h1 font-poppins">
                  {getUITranslation("summary", selectedLanguage)}
                </h1>
                <p className="text-paragraph">
                  {getUITranslation("reviewResponses", selectedLanguage)}
                </p>
              </header>
              {!previewErrorToggle?.[0] && !!missingRequired.length && (
                <ErrorSummary
                  errors={[...fieldErrors, ...groupErrors].sort((a, b) =>
                    a.sectionIndex === b.sectionIndex
                      ? a.order.localeCompare(b.order)
                      : a.sectionIndex - b.sectionIndex,
                  )}
                  selectedLanguage={selectedLanguage}
                />
              )}
              <div className="mt-[5.6rem]">
                {visibleSections.map(({ orderedItems, section }) => (
                  <SummarySection
                    key={section.uri}
                    section={section}
                    onEdit={onSelect ? () => onSelect(section) : undefined}
                    selectedLanguage={selectedLanguage}
                  >
                    {renderOrderedItems({
                      orderedItems,
                      responses,
                      renderGroup: ({ group, ...props }) => (
                        <SummaryGroup
                          {...props}
                          group={translateGroup(group, selectedLanguage)}
                        />
                      ),
                      renderField: ({ field, ...props }) => (
                        <SummaryField
                          {...props}
                          field={translateField(field, selectedLanguage)}
                          selectedLanguage={selectedLanguage}
                        />
                      ),
                      renderGroupFieldSet: ({ group, ...props }) => (
                        <SummaryGroupFieldSet
                          {...props}
                          group={translateGroup(group, selectedLanguage)}
                        />
                      ),
                    })}
                  </SummarySection>
                ))}
                <div className="border-[0.1rem] border-neutral-100 mb-[5.6rem]" />
              </div>
            </SectionButtons>
          </SectionForm>
        </Section>
      </Page>
      {previewErrorToggle && (
        <PreviewFormFooter previewErrorToggle={previewErrorToggle} />
      )}
    </PageWrapper>
  );
};
